import { CalendarDays, GripVertical, LayoutGrid, List, MessageSquare, Plus, UserRound } from "lucide-react";
import {
  TaskPrioritySchema,
  TaskStatusSchema,
  type ProjectProjection,
  type RankCommand,
  type RankResult,
  type TaskPriority,
  type TaskScopedList,
  type TaskStatus,
} from "@mind/contracts/browser";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useMemo, useRef, useState, useEffect, type PointerEvent, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { useAppSession } from "../../app/AppSession.js";
import { ApiError } from "../../api/client.js";
import styles from "./WorkScreen.module.css";
import { applyAuthoritativeRank, applyOptimisticMove } from "./optimistic.js";
import {
  deriveWorkColumns,
  projectScopedMoveNeighbors,
  statusLabelKey,
  type WorkColumn,
  WORK_STATUSES,
} from "./board.js";
import {
  createProjectCommand,
  createRankCommand,
  createTaskCommand,
  createTaskUpdateCommand,
  workApi,
  type WorkTaskListParams,
} from "./workApi.js";
import {
  stripWorkSearch,
  effectiveWorkView,
  validateWorkSearch,
  withWorkProject,
  withWorkStatus,
  withWorkTask,
  withWorkView,
  type WorkSearch,
} from "./searchState.js";
import { withGraphRoot } from "../graph/searchState.js";
import { workQueryKeys } from "./queryKeys.js";

const PAGE_SIZE = 50;
const TASK_LIMIT = 250;
const TASK_PRIORITIES: readonly TaskPriority[] = ["urgent", "high", "medium", "low", "none"];

function browserTimezone(): string {
  try {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return detected?.trim() || "UTC";
  } catch {
    return "UTC";
  }
}

type TaskItem = TaskScopedList["items"][number];

type TaskDetailProps = {
  readonly taskId: string;
  readonly projects: readonly ProjectProjection[];
  readonly taskScope: TaskScopedList | undefined;
  readonly currentAccountId: string | null;
  readonly canMutate: boolean;
  readonly onClose: () => void;
  readonly restoreFocusRef: RefObject<HTMLElement | null>;
};

type TaskDraft = {
  readonly title: string;
  readonly descriptionMarkdown: string;
  readonly priority: TaskPriority;
  readonly dueOn: string;
  readonly assignedToMe: boolean;
  readonly labels: string;
};

type TaskDetailState = "unsaved" | "saving" | "saved" | "stale" | "forbidden";

function readTextField(formData: FormData, name: string): string {
  const value = formData.get(name);
  if (typeof value !== "string") throw new TypeError(`Missing Work field: ${name}`);
  return value;
}

function readTaskDraft(formData: FormData): TaskDraft {
  return {
    title: readTextField(formData, "title"),
    descriptionMarkdown: readTextField(formData, "descriptionMarkdown"),
    priority: TaskPrioritySchema.parse(readTextField(formData, "priority")),
    dueOn: readTextField(formData, "dueOn"),
    assignedToMe: formData.has("assignedToMe"),
    labels: readTextField(formData, "labels"),
  };
}

function normalizeTaskLabels(value: string): string[] {
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const rawLabel of value.split(",")) {
    const label = rawLabel.trim();
    if (label.length > 0 && !seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  }
  return labels;
}

function classifyTaskMutationError(error: unknown): TaskDetailState {
  if (!(error instanceof ApiError)) return "unsaved";
  if (error.code === "FORBIDDEN") return "forbidden";
  if (error.code === "STALE_REVISION" || error.code === "STALE_BOARD_REVISION") return "stale";
  return "unsaved";
}

type PointerState = {
  readonly taskId: string;
  readonly pointerId: number;
};
type TaskQueryData = InfiniteData<TaskScopedList, unknown>;

function mergeTaskPages(data: TaskQueryData | undefined): TaskScopedList | undefined {
  if (data === undefined) return undefined;
  const items = new Map<string, TaskScopedList["items"][number]>();
  const revisions = new Map<string, TaskScopedList["projectBoardRevisions"][number]>();
  for (const page of data.pages) {
    for (const item of page.items) items.set(item.id, item);
    for (const revision of page.projectBoardRevisions) revisions.set(revision.projectId, revision);
  }
  return {
    items: [...items.values()],
    projectBoardRevisions: [...revisions.values()],
    nextCursor: data.pages.at(-1)?.nextCursor ?? null,
  };
}

function replaceTaskPages(data: TaskQueryData, merged: TaskScopedList): TaskQueryData {
  const items = new Map(merged.items.map((item) => [item.id, item]));
  const revisions = new Map(merged.projectBoardRevisions.map((revision) => [revision.projectId, revision]));
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((item) => items.get(item.id) ?? item),
      projectBoardRevisions: page.projectBoardRevisions.map((revision) => revisions.get(revision.projectId) ?? revision),
    })),
  };
}

export function WorkScreen() {
  const { t } = useTranslation();
  const session = useAppSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const rawSearch = useRouterState({ select: (state) => state.location.search });
  const search = validateWorkSearch(rawSearch);
  const workspace = session.workspaces.find((candidate) => candidate.id === search.workspace)
    ?? session.selectedWorkspace;
  const currentAccountId = session.identity?.effective.id ?? null;
  const workspaceId = workspace?.id ?? "";
  const [projectName, setProjectName] = useState("");
  const projectTimezone = useMemo(browserTimezone, []);
  const [projectCreateOpen, setProjectCreateOpen] = useState(false);
  const [quickTitle, setQuickTitle] = useState("");
  const [quickStatus, setQuickStatus] = useState<TaskStatus>(search.status ?? "inbox");
  const [announcement, setAnnouncement] = useState("");
  const [pointerTaskId, setPointerTaskId] = useState<string | null>(null);
  const pointerRef = useRef<PointerState | null>(null);
  const taskRestoreFocusRef = useRef<HTMLElement | null>(null);
  const projectDialogRef = useRef<HTMLDialogElement | null>(null);
  const projectSelectRef = useRef<HTMLSelectElement | null>(null);
  const quickTitleRef = useRef<HTMLInputElement | null>(null);
  const [isCompactViewport, setIsCompactViewport] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 1250px)").matches);
  const canMutate = workspace !== null && workspace.currentRole !== "viewer";
  const projectCreateTriggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const dialog = projectDialogRef.current;
    if (dialog === null) return;
    if (projectCreateOpen && !dialog.open) dialog.showModal();
    if (!projectCreateOpen && dialog.open) dialog.close();
  }, [projectCreateOpen]);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 1250px)");
    const update = (): void => setIsCompactViewport(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  const projectsQuery = useQuery({
    queryKey: workQueryKeys.projects(workspaceId),
    enabled: workspaceId.length > 0,
    queryFn: ({ signal }) => workApi.listProjects(workspaceId, false, signal),
  });
  const projects = projectsQuery.data?.items ?? [];
  const projectById = useMemo(
    () => Object.fromEntries(projects.map((project) => [project.id, project])),
    [projects],
  );
  const myTasks = search.assignee === "me";
  const selectedProjectId = search.project ?? (myTasks ? undefined : projects[0]?.id);
  const taskParams: WorkTaskListParams = {
    ...(workspaceId.length === 0 ? {} : { workspaceId }),
    ...(selectedProjectId === undefined ? {} : { projectId: selectedProjectId }),
    ...(myTasks ? { assignee: "me" as const } : {}),
    includeArchived: false,
    limit: TASK_LIMIT,
  };
  const taskQueryKey = workQueryKeys.tasks(taskParams);
  const tasksQuery = useInfiniteQuery({
    queryKey: taskQueryKey,
    enabled: workspaceId.length > 0 && (myTasks || selectedProjectId !== undefined),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => workApi.listTasks({ ...taskParams, cursor: pageParam }, signal),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  const taskScope = useMemo(() => mergeTaskPages(tasksQuery.data), [tasksQuery.data]);
  const taskItems = taskScope?.items ?? [];
  const scopeProjectId = myTasks ? search.project : selectedProjectId;
  const allColumns = useMemo(
    () => deriveWorkColumns(taskItems, scopeProjectId === undefined ? {} : { project: scopeProjectId }),
    [scopeProjectId, taskItems],
  );
  const columns = useMemo(
    () => deriveWorkColumns(taskItems, {
      ...(scopeProjectId === undefined ? {} : { project: scopeProjectId }),
      ...(search.status === undefined ? {} : { status: search.status }),
      ...(search.filter === undefined ? {} : { filter: search.filter }),
    }),
    [scopeProjectId, search.filter, search.status, taskItems],
  );
  const navigateSearch = (next: WorkSearch): void => {
    void navigate({ to: "/work", search: next });
  };

  const openTask = (taskId: string, trigger: HTMLButtonElement): void => {
    taskRestoreFocusRef.current = trigger;
    void navigateSearch(withWorkTask(search, taskId));
  };

  const createProject = useMutation({
    retry: false,
    mutationFn: () => {
      if (!canMutate) throw new Error("Work is view only");
      if (workspaceId.length === 0) throw new Error("Work workspace is unavailable");
      return workApi.createProject(createProjectCommand(workspaceId, projectName, projectTimezone));
    },
    onSuccess: async (project) => {
      setProjectName("");
      setProjectCreateOpen(false);
      await queryClient.invalidateQueries({ queryKey: workQueryKeys.projects(workspaceId) });
      await navigateSearch(withWorkProject(search, project.id));
      window.requestAnimationFrame(() => quickTitleRef.current?.focus());
    },
  });

  const createTask = useMutation({
    retry: false,
    mutationFn: () => {
      if (!canMutate) throw new Error("Work is view only");
      if (selectedProjectId === undefined) throw new Error("A project is required to create a task");
      const project = projectById[selectedProjectId];
      if (project === undefined) throw new Error("Project authority is unavailable");
      const boardRevision = taskScope?.projectBoardRevisions.find((revision) => revision.projectId === project.id)?.boardRevision
        ?? project.boardRevision;
      const destination = allColumns.find((column) => column.status === quickStatus)?.items ?? [];
      const last = destination.at(-1);
      return workApi.createTask(createTaskCommand({
        projectId: project.id,
        title: quickTitle,
        descriptionMarkdown: "",
        status: quickStatus,
        priority: "none",
        dueOn: null,
        beforeTaskId: last?.id ?? null,
        afterTaskId: null,
        boardRevision,
      }));
    },
    onSuccess: async (result) => {
      setQuickTitle("");
      await queryClient.invalidateQueries({ queryKey: workQueryKeys.tasksRoot });
      await navigateSearch(withWorkTask(search, result.taskId));
    },
  });

  const moveTask = useMutation<RankResult, Error, RankCommand, { readonly previous: TaskQueryData | undefined }>({
    retry: false,
    mutationFn: (command: RankCommand): Promise<RankResult> => workApi.rankTask(command),
    onMutate: async (command) => {
      await queryClient.cancelQueries({ queryKey: taskQueryKey });
      const previous = queryClient.getQueryData<TaskQueryData>(taskQueryKey);
      const merged = mergeTaskPages(previous);
      if (previous !== undefined && merged !== undefined) {
        queryClient.setQueryData(taskQueryKey, replaceTaskPages(previous, applyOptimisticMove(merged, command)));
      }
      return { previous };
    },
    onError: (_error, _command, context) => {
      if (context?.previous !== undefined) queryClient.setQueryData(taskQueryKey, context.previous);
      setAnnouncement(t("work.moveConflict"));
    },
    onSuccess: (result, command) => {
      const currentData = queryClient.getQueryData<TaskQueryData>(taskQueryKey);
      const current = mergeTaskPages(currentData);
      const moved = current?.items.find((task) => task.id === command.taskId);
      if (currentData !== undefined && current !== undefined) {
        queryClient.setQueryData(taskQueryKey, replaceTaskPages(currentData, applyAuthoritativeRank(current, result)));
      }
      if (moved !== undefined) {
        const projectKey = workQueryKeys.project(moved.projectId);
        const project = queryClient.getQueryData<ProjectProjection>(projectKey);
        if (project !== undefined) {
          queryClient.setQueryData<ProjectProjection>(projectKey, { ...project, boardRevision: result.boardRevision });
        }
      }
      setAnnouncement(t("work.moveSaved"));
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: workQueryKeys.tasksRoot });
      await queryClient.invalidateQueries({ queryKey: workQueryKeys.projectRoot });
      if (workspaceId.length > 0) await queryClient.invalidateQueries({ queryKey: workQueryKeys.projects(workspaceId) });
    },
  });

  const buildMoveCommand = (task: TaskItem, status: TaskStatus, insertionIndex?: number): RankCommand => {
    const project = projectById[task.projectId];
    if (project === undefined) throw new Error("Project authority is unavailable");
    const boardRevision = taskScope?.projectBoardRevisions.find((revision) => revision.projectId === task.projectId)?.boardRevision
      ?? project.boardRevision;
    const neighbors = projectScopedMoveNeighbors(taskItems, task.projectId, task.id, status, insertionIndex);
    return createRankCommand({
      taskId: task.id,
      fromStatus: task.status,
      toStatus: status,
      beforeTaskId: neighbors.beforeTaskId,
      afterTaskId: neighbors.afterTaskId,
      taskRevision: task.revision,
      boardRevision,
    });
  };

  const moveTaskTo = (task: TaskItem, status: TaskStatus, insertionIndex?: number): void => {
    if (!canMutate || (status === task.status && insertionIndex === undefined)) return;
    if (tasksQuery.hasNextPage) {
      setAnnouncement(t("work.loadMoreBeforeMove"));
      return;
    }
    if (moveTask.isPending) {
      setAnnouncement(t("work.movePending"));
      return;
    }
    try {
      moveTask.mutate(buildMoveCommand(task, status, insertionIndex));
    } catch (error) {
      setAnnouncement(error instanceof Error ? error.message : t("work.error"));
    }
  };

  const moveTaskRelative = (task: TaskItem, direction: "earlier" | "later"): void => {
    if (tasksQuery.hasNextPage) {
      setAnnouncement(t("work.loadMoreBeforeMove"));
      return;
    }
    const destination = allColumns.find((column) => column.status === task.status)?.items.filter((item) => item.projectId === task.projectId) ?? [];
    const index = destination.findIndex((item) => item.id === task.id);
    if (index < 0) return;
    const insertionIndex = direction === "earlier" ? index - 1 : index + 1;
    if (insertionIndex < 0 || insertionIndex >= destination.length) {
      setAnnouncement(t("work.moveBoundary"));
      return;
    }
    moveTaskTo(task, task.status, insertionIndex);
  };

  const resetPointerMove = (): void => {
    pointerRef.current = null;
    setPointerTaskId(null);
  };

  const handlePointerDown = (taskId: string, event: PointerEvent<HTMLElement>): void => {
    if (!canMutate || (event.pointerType === "mouse" && event.button !== 0) || pointerRef.current !== null) return;
    pointerRef.current = { taskId, pointerId: event.pointerId };
    setPointerTaskId(taskId);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const finishPointerMove = (event: PointerEvent<HTMLElement>): void => {
    const pointer = pointerRef.current;
    if (pointer === null || pointer.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    const point = document.elementFromPoint(event.clientX, event.clientY);
    const statusValue = point?.closest<HTMLElement>("[data-work-status]")?.dataset["workStatus"];
    const task = taskItems.find((item) => item.id === pointer.taskId);
    const parsedStatus = TaskStatusSchema.safeParse(statusValue);
    let insertionIndex: number | undefined;
    if (parsedStatus.success) {
      const destination = task === undefined
        ? []
        : allColumns.find((column) => column.status === parsedStatus.data)?.items.filter((item) => item.projectId === task.projectId) ?? [];
      const targetElement = point?.closest<HTMLElement>("[data-work-task-id]");
      const targetId = targetElement?.dataset["workTaskId"];
      const targetIndex = targetId === undefined ? -1 : destination.findIndex((item) => item.id === targetId);
      if (targetIndex < 0) {
        insertionIndex = destination.length;
      } else {
        const bounds = targetElement?.getBoundingClientRect();
        insertionIndex = bounds !== undefined && event.clientY >= bounds.top + bounds.height / 2
          ? targetIndex + 1
          : targetIndex;
      }
    }
    resetPointerMove();
    if (task !== undefined && parsedStatus.success) moveTaskTo(task, parsedStatus.data, insertionIndex);
  };

  if (workspace === null) {
    return (
      <section className={styles.empty} aria-labelledby="work-title">
        <h1 id="work-title">{t("work.title")}</h1>
        <p>{t("work.noWorkspace")}</p>
      </section>
    );
  }

  const effectiveView = effectiveWorkView(search, isCompactViewport ? "compact" : "wide");
  const showBoard = effectiveView === "board";
  const activeFilterCount = [search.filter, search.status, search.assignee].filter((value) => value !== undefined).length;
  const visibleTaskCount = columns.reduce((total, column) => total + column.items.length, 0);
  const projectEmpty = tasksQuery.isSuccess && !myTasks && selectedProjectId !== undefined && taskItems.length === 0 && activeFilterCount === 0;
  const myTasksEmpty = tasksQuery.isSuccess && myTasks && taskItems.length === 0;
  const filterNoMatch = tasksQuery.isSuccess && activeFilterCount > 0 && !myTasksEmpty && visibleTaskCount === 0;
  const clearFilters = (): void => {
    void navigateSearch(stripWorkSearch({ ...search, filter: undefined, status: undefined, assignee: undefined }));
  };

  return (
    <section className={styles.work} aria-labelledby="work-title">
      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>{t("work.title")}</p>
          <h1 id="work-title">
            {myTasks
              ? t("work.myTasks")
              : selectedProjectId === undefined
                ? t("work.noProjectsTitle")
                : projectById[selectedProjectId]?.name ?? t("work.chooseProject")}
          </h1>
        </div>
        <div className={styles.headerActions}>
          {projects.length > 0 ? (
            <label className={styles.compactField}>
              <span>{t("work.project")}</span>
              <select
                ref={projectSelectRef}
                value={selectedProjectId ?? ""}
                onChange={(event) => void navigateSearch(withWorkProject(search, event.currentTarget.value || null))}
              >
                <option value="">{myTasks ? t("work.allProjects") : t("work.chooseProject")}</option>
                {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
              </select>
            </label>
          ) : null}
          {projects.length > 0 && canMutate ? (
            <button
              ref={projectCreateTriggerRef}
              type="button"
              className={styles.secondaryButton}
              aria-controls="work-project-create"
              aria-expanded={projectCreateOpen}
              onClick={() => setProjectCreateOpen(true)}
            >
              <Plus aria-hidden="true" size={17} />
              {t("work.createProject")}
            </button>
          ) : null}
        </div>
      </header>
      {projects.length > 0 ? (
        <div className={styles.toolbar}>
          {canMutate && selectedProjectId !== undefined ? (
            <form className={styles.quickCreate} onSubmit={(event) => { event.preventDefault(); if (quickTitle.trim().length > 0) createTask.mutate(); }}>
              <label>
                <span>{t("work.quickCreate")}</span>
                <input ref={quickTitleRef} value={quickTitle} maxLength={500} onChange={(event) => setQuickTitle(event.currentTarget.value)} placeholder={t("work.quickCreatePlaceholder")} />
              </label>
              <label>
                <span>{t("work.status")}</span>
                <select aria-label={t("work.status")} value={quickStatus} onChange={(event) => setQuickStatus(TaskStatusSchema.parse(event.currentTarget.value))}>
                  {WORK_STATUSES.map((status) => <option key={status} value={status}>{t(statusLabelKey(status))}</option>)}
                </select>
              </label>
              <button type="submit" className={styles.primaryButton} aria-label={t("work.addTask")} disabled={createTask.isPending}>
                <Plus aria-hidden="true" size={18} />
                {t("common.create")}
              </button>
            </form>
          ) : null}
          <details
            className={styles.taskTools}
            open={(search.filter !== undefined || search.status !== undefined || myTasks) || undefined}
          >
            <summary>{t("work.filter")}</summary>
            <div className={styles.taskToolsPanel}>
              <div className={styles.filterControls}>
                <label className={styles.searchField}>
                  <span>{t("work.filter")}</span>
                  <input
                    value={search.filter ?? ""}
                    maxLength={100}
                    onChange={(event) => void navigateSearch(stripWorkSearch({ ...search, filter: event.currentTarget.value }))}
                    placeholder={t("work.filterPlaceholder")}
                  />
                </label>
                <label className={styles.compactField}>
                  <span>{t("work.statusFilter")}</span>
                  <select value={search.status ?? ""} onChange={(event) => { const value = event.currentTarget.value; void navigateSearch(withWorkStatus(search, value.length === 0 ? null : TaskStatusSchema.parse(value))); }}>
                    <option value="">{t("work.allStatuses")}</option>
                    {WORK_STATUSES.map((status) => <option key={status} value={status}>{t(statusLabelKey(status))}</option>)}
                  </select>
                </label>
              </div>
              <div className={styles.filterActions}>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  aria-pressed={myTasks}
                  onClick={() => void navigateSearch(stripWorkSearch({ ...search, assignee: myTasks ? undefined : "me", task: undefined }))}
                >
                  <UserRound aria-hidden="true" size={17} />
                  {t("work.myTasks")}
                </button>
              </div>
            </div>
          </details>
          <div className={styles.filterActions}>
            <button type="button" className={styles.secondaryButton} aria-pressed={showBoard} onClick={() => void navigateSearch(withWorkView(search, "board"))}>
              <LayoutGrid aria-hidden="true" size={17} />
              {t("work.boardView")}
            </button>
            <button type="button" className={styles.secondaryButton} aria-pressed={!showBoard} onClick={() => void navigateSearch(withWorkView(search, "list"))}>
              <List aria-hidden="true" size={17} />
              {t("work.listView")}
            </button>
          </div>
        </div>
      ) : null}

      {canMutate ? (
      <dialog
        ref={projectDialogRef}
        id="work-project-create"
        className={styles.projectDialog}
        aria-labelledby="work-project-create-title"
        onClose={() => {
          setProjectCreateOpen(false);
          window.requestAnimationFrame(() => projectCreateTriggerRef.current?.focus());
        }}
      >
        <form className={styles.createProject} onSubmit={(event) => { event.preventDefault(); createProject.mutate(); }}>
          <header className={styles.dialogHeader}>
            <h2 id="work-project-create-title">{t("work.createProject")}</h2>
            <button type="button" className={styles.secondaryButton} onClick={() => projectDialogRef.current?.close()}>{t("common.close")}</button>
          </header>
          <label>
            <span>{t("work.projectName")}</span>
            <input value={projectName} onChange={(event) => setProjectName(event.currentTarget.value)} maxLength={200} required />
          </label>
          <div className={styles.projectDialogActions}>
            <button type="submit" className={styles.primaryButton} disabled={createProject.isPending}>{t("common.create")}</button>
          </div>
          {createProject.error !== null ? <p className={styles.error} role="alert">{t("work.error")}</p> : null}
        </form>
      </dialog>
      ) : null}

      {projectsQuery.isPending ? <p className={styles.state} role="status">{t("work.loading")}</p> : null}
      {projectsQuery.error !== null ? <p className={styles.error} role="alert">{t("work.error")}</p> : null}
      {tasksQuery.isPending ? <p className={styles.state} role="status">{t("work.loading")}</p> : null}
      {tasksQuery.error !== null ? <p className={styles.error} role="alert">{t("work.error")}</p> : null}
      {tasksQuery.hasNextPage ? (
        <div className={styles.pagination}>
          <p>{t("work.incompleteResults")}</p>
          <button type="button" className={styles.secondaryButton} onClick={() => void tasksQuery.fetchNextPage()} disabled={tasksQuery.isFetchingNextPage}>
            {tasksQuery.isFetchingNextPage ? t("work.loadingMoreTasks") : t("work.loadMoreTasks")}
          </button>
        </div>
      ) : null}
      {projectsQuery.isSuccess && projects.length === 0 ? (
        <section className={styles.empty} aria-labelledby="work-empty-title">
          <h2 id="work-empty-title">{t("work.noProjectsTitle")}</h2>
          <p>{canMutate ? t("work.noProjectsDescription") : t("work.noProjectsReadOnly")}</p>
          {canMutate ? (
            <div className={styles.filterActions}>
              <button type="button" className={styles.primaryButton} onClick={() => setProjectCreateOpen(true)}>
                <Plus aria-hidden="true" size={17} />
                {t("work.createProject")}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}
      {myTasksEmpty ? (
        <section className={styles.empty} aria-labelledby="work-empty-title">
          <h2 id="work-empty-title">{t("work.noMyTasksTitle")}</h2>
          <p>{t("work.noMyTasksDescription")}</p>
          <div className={styles.filterActions}>
            <button type="button" className={styles.secondaryButton} onClick={() => projectSelectRef.current?.focus()}>{t("work.chooseProject")}</button>
            <button type="button" className={styles.primaryButton} onClick={() => void navigateSearch(stripWorkSearch({ ...search, assignee: undefined, project: undefined }))}>{t("work.viewProjectTasks")}</button>
          </div>
        </section>
      ) : null}
      {projectEmpty ? (
        <section className={styles.empty} aria-labelledby="work-empty-title">
          <h2 id="work-empty-title">{t("work.noTasksForProjectTitle")}</h2>
          <p>{canMutate ? t("work.noTasksForProjectDescription") : t("work.noTasksForProjectReadOnly")}</p>
          {canMutate ? <button type="button" className={styles.primaryButton} onClick={() => quickTitleRef.current?.focus()}>{t("work.addTask")}</button> : null}
        </section>
      ) : null}
      {filterNoMatch ? (
        <section className={styles.empty} aria-labelledby="work-empty-title">
          <h2 id="work-empty-title">{t("work.noFilteredTasksTitle")}</h2>
          <p>{t("work.noFilteredTasksDescription")}</p>
          <button type="button" className={styles.secondaryButton} onClick={clearFilters}>{t("work.clearFilters")}</button>
        </section>
      ) : null}

      {projects.length > 0 && !tasksQuery.isPending && tasksQuery.error === null && !projectEmpty && !myTasksEmpty && !filterNoMatch ? (
        <>
          {showBoard ? (
            <div className={styles.board} aria-label={t("work.board")}>
              {columns.map((column) => (
                <WorkColumn
                  key={column.status}
                  column={column}
                  movingTaskId={pointerTaskId}
                  onOpen={openTask}
                  onMove={(task, status) => moveTaskTo(task, status)}
                  onMoveEarlier={(task) => moveTaskRelative(task, "earlier")}
                  onMoveLater={(task) => moveTaskRelative(task, "later")}
                  canMutate={canMutate}
                  onPointerDown={handlePointerDown}
                  onPointerUp={finishPointerMove}
                  onPointerCancel={resetPointerMove}
                />
              ))}
            </div>
          ) : null}
          <WorkList
            columns={columns}
            visibleOnDesktop={!showBoard}
            movingTaskId={pointerTaskId}
            onOpen={openTask}
            onMove={(task, status) => moveTaskTo(task, status)}
            onPointerDown={handlePointerDown}
            onPointerUp={finishPointerMove}
            onPointerCancel={resetPointerMove}
            onMoveEarlier={(task) => moveTaskRelative(task, "earlier")}
            onMoveLater={(task) => moveTaskRelative(task, "later")}
            canMutate={canMutate}
          />
        </>
      ) : null}

      <p className={styles.liveRegion} aria-live="polite">{announcement}</p>
      {search.task !== undefined ? (
        <TaskDetail
          taskId={search.task}
          projects={projects}
          taskScope={taskScope}
          currentAccountId={currentAccountId}
          canMutate={canMutate}
          onClose={() => void navigateSearch(withWorkTask(search, null))}
          restoreFocusRef={taskRestoreFocusRef}
        />
      ) : null}
    </section>
  );
}

type ColumnProps = {
  readonly column: WorkColumn;
  readonly movingTaskId: string | null;
  readonly onOpen: (taskId: string, trigger: HTMLButtonElement) => void;
  readonly onMove: (task: TaskItem, status: TaskStatus) => void;
  readonly onMoveEarlier: (task: TaskItem) => void;
  readonly onMoveLater: (task: TaskItem) => void;
  readonly canMutate: boolean;
  readonly onPointerDown: (taskId: string, event: PointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: () => void;
};
type WorkListProps = Omit<ColumnProps, "column"> & { readonly columns: readonly WorkColumn[]; readonly visibleOnDesktop: boolean };

function WorkColumn({ column, movingTaskId, onOpen, onMove, onMoveEarlier, onMoveLater, canMutate, onPointerDown, onPointerUp, onPointerCancel }: ColumnProps) {
  const { t } = useTranslation();
  return (
    <section className={styles.column} data-work-status={column.status} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel}>
      <header>
        <h2 id={`work-status-${column.status}`}><span>{t(statusLabelKey(column.status))}</span><span>{column.items.length}</span></h2>
        {movingTaskId !== null ? <span className={styles.dropHint}>{t("work.dropHere")}</span> : null}
      </header>
      <div className={styles.cards} aria-labelledby={`work-status-${column.status}`}>
        {column.items.length === 0 ? <p className={styles.columnEmpty}>{t("work.noTasks")}</p> : column.items.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            isMoving={movingTaskId === task.id}
            onOpen={(trigger) => onOpen(task.id, trigger)}
            onMove={(status) => onMove(task, status)}
            onMoveEarlier={() => onMoveEarlier(task)}
            onMoveLater={() => onMoveLater(task)}
            canMutate={canMutate}
            onPointerDown={(event) => onPointerDown(task.id, event)}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
          />
        ))}
      </div>
    </section>
  );
}

type TaskCardProps = {
  readonly task: TaskItem;
  readonly isMoving: boolean;
  readonly onOpen: (trigger: HTMLButtonElement) => void;
  readonly onMove: (status: TaskStatus) => void;
  readonly onMoveEarlier: () => void;
  readonly onMoveLater: () => void;
  readonly canMutate: boolean;
  readonly onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: () => void;
};

function TaskCard({ task, isMoving, onOpen, onMove, onMoveEarlier, onMoveLater, canMutate, onPointerDown, onPointerUp, onPointerCancel }: TaskCardProps) {
  const { t } = useTranslation();
  return (
    <article className={styles.card} data-work-task-id={task.id} data-moving={isMoving ? "true" : undefined}>
      <div className={styles.cardHeader}>
        {canMutate ? (
          <button type="button" className={styles.dragHandle} onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel} aria-label={t("work.dragHandle")}>
            <GripVertical aria-hidden="true" size={18} />
          </button>
        ) : null}
        <button type="button" className={styles.cardTitle} onClick={(event) => onOpen(event.currentTarget)}>{task.title}</button>
      </div>
      <div className={styles.cardMeta}>
        <span data-priority={task.priority}>{t(`work.priorities.${task.priority}`)}</span>
        {task.dueOn !== null ? <span><CalendarDays aria-hidden="true" size={14} />{task.dueOn}</span> : null}
      </div>
      {task.labels.length > 0 ? <div className={styles.labels}>{task.labels.map((label) => <span key={label}>{label}</span>)}</div> : null}
      {task.assigneeIds.length > 0 || task.commentCount > 0 ? (
        <div className={styles.cardFooter}>
          {task.assigneeIds.length > 0 ? <span><UserRound aria-hidden="true" size={14} />{task.assigneeIds.length}</span> : null}
          {task.commentCount > 0 ? <span><MessageSquare aria-hidden="true" size={14} />{task.commentCount}</span> : null}
        </div>
      ) : null}
      {canMutate ? (
        <>
          <label className={styles.moveMenu}>
            <span className={styles.visuallyHidden}>{t("work.moveTo")}</span>
            <select aria-label={t("work.moveTo")} value={task.status} onChange={(event) => onMove(TaskStatusSchema.parse(event.currentTarget.value))}>
              {WORK_STATUSES.map((status) => <option key={status} value={status}>{t(statusLabelKey(status))}</option>)}
            </select>
          </label>
          <div className={styles.moveActions}>
            <button type="button" className={styles.secondaryButton} onClick={onMoveEarlier}>{t("work.moveEarlier")}</button>
            <button type="button" className={styles.secondaryButton} onClick={onMoveLater}>{t("work.moveLater")}</button>
          </div>
        </>
      ) : null}
    </article>
  );
}
function WorkList({
  columns,
  visibleOnDesktop,
  movingTaskId,
  onOpen,
  onMove,
  onMoveEarlier,
  onMoveLater,
  canMutate,
  onPointerDown,
  onPointerUp,
  onPointerCancel,
}: WorkListProps) {
  const { t } = useTranslation();
  return (
    <div className={styles.desktopList} data-visible-on-desktop={visibleOnDesktop ? "true" : "false"} aria-label={t("work.listView")}>
      {columns.map((column) => (
        <section key={column.status} className={styles.mobileGroup} data-work-status={column.status} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel}>
          <h2><span>{t(statusLabelKey(column.status))}</span><span>{column.items.length}</span></h2>
          {column.items.length === 0 ? <p>{t("work.noTasks")}</p> : column.items.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              isMoving={movingTaskId === task.id}
              onOpen={(trigger) => onOpen(task.id, trigger)}
              onMove={(status) => onMove(task, status)}
              onMoveEarlier={() => onMoveEarlier(task)}
              onMoveLater={() => onMoveLater(task)}
              canMutate={canMutate}
              onPointerDown={(event) => onPointerDown(task.id, event)}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerCancel}
            />
          ))}
        </section>
      ))}
    </div>
  );
}
function TaskRow({
  task,
  isMoving,
  onOpen,
  onMove,
  onMoveEarlier,
  onMoveLater,
  canMutate,
  onPointerDown,
  onPointerUp,
  onPointerCancel,
}: TaskCardProps) {
  const { t } = useTranslation();
  return (
    <article className={styles.row} data-work-task-id={task.id} data-moving={isMoving ? "true" : undefined}>
      <div className={styles.rowHeader}>
        {canMutate ? (
          <button type="button" className={styles.dragHandle} onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel} aria-label={t("work.dragHandle")}>
            <GripVertical aria-hidden="true" size={18} />
          </button>
        ) : null}
        <button type="button" className={styles.rowTitle} onClick={(event) => onOpen(event.currentTarget)}>{task.title}</button>
      </div>
      <span>{t(`work.priorities.${task.priority}`)}</span>
      <span>{task.dueOn ?? t("work.noDeadline")}</span>
      {canMutate ? (
        <>
          <label className={styles.rowMove}>
            <span className={styles.visuallyHidden}>{t("work.moveTo")}</span>
            <select aria-label={t("work.moveTo")} value={task.status} onChange={(event) => onMove(TaskStatusSchema.parse(event.currentTarget.value))}>
              {WORK_STATUSES.map((status) => <option key={status} value={status}>{t(statusLabelKey(status))}</option>)}
            </select>
          </label>
          <div className={styles.moveActions}>
            <button type="button" className={styles.secondaryButton} onClick={onMoveEarlier}>{t("work.moveEarlier")}</button>
            <button type="button" className={styles.secondaryButton} onClick={onMoveLater}>{t("work.moveLater")}</button>
          </div>
        </>
      ) : null}
    </article>
  );
}

function TaskDetail({ taskId, projects, taskScope, currentAccountId, canMutate, onClose, restoreFocusRef }: TaskDetailProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const detailRef = useRef<HTMLElement | null>(null);
  const [detailState, setDetailState] = useState<TaskDetailState>(canMutate ? "saved" : "forbidden");
  const [nextStatus, setNextStatus] = useState<TaskStatus>("inbox");
  const [comment, setComment] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      detailRef.current?.querySelector<HTMLElement>("#task-detail-title")?.focus();
    });
    return () => {
      const restoreTarget = restoreFocusRef.current;
      if (restoreTarget?.isConnected) restoreTarget.focus();
      window.cancelAnimationFrame(frame);
    };
  }, [restoreFocusRef, taskId]);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const queryClient = useQueryClient();
  const taskQuery = useQuery({
    queryKey: workQueryKeys.task(taskId),
    queryFn: ({ signal }) => workApi.getTask(taskId, signal),
    enabled: taskId.length > 0,
  });
  const task = taskQuery.data;
  const projectId = task?.projectId ?? "none";
  const projectQuery = useQuery({
    queryKey: workQueryKeys.project(projectId),
    enabled: task !== undefined,
    queryFn: ({ signal }) => workApi.getProject(projectId, signal),
  });
  const scopeParams: WorkTaskListParams = task === undefined ? {} : {
    workspaceId: task.workspaceId,
    projectId: task.projectId,
    includeArchived: false,
    limit: TASK_LIMIT,
  };
  const scopeQuery = useQuery({
    queryKey: workQueryKeys.taskScope(taskId),
    enabled: task !== undefined && !task.archived,
    queryFn: ({ signal }) => workApi.listTasks(scopeParams, signal),
  });
  const scope = taskScope ?? scopeQuery.data;
  const scopeComplete = taskScope !== undefined ? taskScope.nextCursor === null : scopeQuery.data?.nextCursor === null;

  useEffect(() => {
    if (task !== undefined) setNextStatus(task.status);
  }, [task?.id, task?.revision, task?.status]);
  useEffect(() => {
    if (!canMutate) setDetailState("forbidden");
    else if (detailState === "forbidden") setDetailState("saved");
  }, [canMutate, detailState]);

  const comments = useInfiniteQuery({
    queryKey: workQueryKeys.comments(taskId),
    enabled: task !== undefined,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => workApi.listComments(taskId, { cursor: pageParam, limit: PAGE_SIZE }, signal),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    maxPages: 3,
  });
  const activity = useInfiniteQuery({
    queryKey: workQueryKeys.activity(taskId),
    enabled: task !== undefined,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => workApi.listActivity(taskId, { cursor: pageParam, limit: PAGE_SIZE }, signal),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    maxPages: 3,
  });

  const saveTask = useMutation({
    retry: false,
    mutationFn: async (draft: TaskDraft) => {
      if (!canMutate) throw new Error("Work is view only");
      if (task === undefined) throw new Error("Task authority is unavailable");
      const assigneeIds = new Set(task.assigneeIds);
      if (currentAccountId !== null) {
        if (draft.assignedToMe) assigneeIds.add(currentAccountId);
        else assigneeIds.delete(currentAccountId);
      }
      return workApi.updateTask(task.id, createTaskUpdateCommand(task.id, task.revision, {
        title: draft.title,
        descriptionMarkdown: draft.descriptionMarkdown,
        priority: draft.priority,
        dueOn: draft.dueOn.trim().length === 0 ? null : draft.dueOn,
        assigneeIds: Array.from(assigneeIds),
        labels: normalizeTaskLabels(draft.labels),
      }));
    },
    onSuccess: async () => {
      setFormError(null);
      setDetailState("saved");
      await queryClient.invalidateQueries({ queryKey: workQueryKeys.all });
    },
    onError: (error) => {
      setDetailState(classifyTaskMutationError(error));
      setFormError(t("work.error"));
    },
  });

  const moveStatus = useMutation({
    retry: false,
    mutationFn: (status: TaskStatus) => {
      if (!canMutate) throw new Error("Work is view only");
      if (task === undefined || scope === undefined) throw new Error("Task movement authority is unavailable");
      if (!scopeComplete) throw new Error(t("work.loadMoreBeforeMove"));
      if (task.archived) throw new Error(t("work.archivedState"));
      const project = projectQuery.data ?? projects.find((candidate) => candidate.id === task.projectId);
      if (project === undefined) throw new Error("Project authority is unavailable");
      const neighbors = projectScopedMoveNeighbors(scope.items, task.projectId, task.id, status);
      const boardRevision = scope.projectBoardRevisions.find((revision) => revision.projectId === task.projectId)?.boardRevision ?? project.boardRevision;
      return workApi.rankTask(createRankCommand({
        taskId: task.id,
        fromStatus: task.status,
        toStatus: status,
        beforeTaskId: neighbors.beforeTaskId,
        afterTaskId: neighbors.afterTaskId,
        taskRevision: task.revision,
        boardRevision,
      }));
    },
    onSuccess: async (_result, status) => {
      setNextStatus(status);
      setFormError(null);
      setDetailState("saved");
      await queryClient.invalidateQueries({ queryKey: workQueryKeys.all });
    },
    onError: (error) => {
      setDetailState(classifyTaskMutationError(error));
      setFormError(error instanceof Error ? error.message : t("work.error"));
    },
  });

  const archiveTask = useMutation({
    retry: false,
    mutationFn: () => {
      if (!canMutate) throw new Error("Work is view only");
      if (task === undefined || projectQuery.data === undefined) throw new Error("Archive authority is unavailable");
      return workApi.updateTask(task.id, createTaskUpdateCommand(task.id, task.revision, {
        archived: !task.archived,
        boardRevision: projectQuery.data.boardRevision,
      }));
    },
    onSuccess: async () => {
      setFormError(null);
      setDetailState("saved");
      await queryClient.invalidateQueries({ queryKey: workQueryKeys.all });
      onClose();
    },
    onError: (error) => {
      setDetailState(classifyTaskMutationError(error));
      setFormError(t("work.error"));
    },
  });

  const addComment = useMutation({
    retry: false,
    mutationFn: () => {
      if (!canMutate) throw new Error("Work is view only");
      return workApi.createComment(taskId, {
        commandId: crypto.randomUUID(),
        commentId: crypto.randomUUID(),
        bodyMarkdown: comment,
      });
    },
    onSuccess: async () => {
      setComment("");
      setFormError(null);
      setDetailState("saved");
      await queryClient.invalidateQueries({ queryKey: workQueryKeys.comments(taskId) });
      await queryClient.invalidateQueries({ queryKey: workQueryKeys.activity(taskId) });
      await queryClient.invalidateQueries({ queryKey: workQueryKeys.tasksRoot });
    },
    onError: (error) => {
      setDetailState(classifyTaskMutationError(error));
      setFormError(t("work.error"));
    },
  });

  const projectName = projectQuery.data?.name ?? projects.find((project) => project.id === task?.projectId)?.name;

  return (
    <aside ref={detailRef} className={styles.detail} aria-labelledby="task-detail-title" tabIndex={-1}>
      <header className={styles.detailHeader}>
        <div>
          <p className={styles.eyebrow}>{projectName ?? t("work.taskDetail")}</p>
          <h2 id="task-detail-title" tabIndex={-1}>{task?.title ?? t("work.loading")}</h2>
        </div>
        <div className={styles.detailActions}>
          {task !== undefined ? (
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => void navigate({ to: "/graph", search: withGraphRoot({ workspace: task.workspaceId }, { type: "task", id: task.id, revision: task.revision }) })}
            >
              {t("work.showInConnections")}
            </button>
          ) : null}
          <button type="button" className={styles.secondaryButton} onClick={onClose}>{t("common.close")}</button>
        </div>
      </header>
      <p className={styles.detailStatus} data-state={detailState} role="status">{t(`work.detailStates.${detailState}`)}</p>
      {taskQuery.isPending ? <p className={styles.state} role="status">{t("work.loading")}</p> : null}
      {taskQuery.error !== null ? <p className={styles.error} role="alert">{t("work.error")}</p> : null}
      {task !== undefined ? (
        <>
          {task.archived ? <p className={styles.state}>{t("work.archivedState")}</p> : null}
          {canMutate ? (
            <>
              <div className={styles.statusMove}>
                <label>
                  <span>{t("work.status")}</span>
                  <select value={nextStatus} onChange={(event) => setNextStatus(TaskStatusSchema.parse(event.currentTarget.value))} disabled={task.archived || moveStatus.isPending}>
                    {WORK_STATUSES.map((value) => <option key={value} value={value}>{t(statusLabelKey(value))}</option>)}
                  </select>
                </label>
                <button type="button" className={styles.secondaryButton} onClick={() => moveStatus.mutate(nextStatus)} disabled={task.archived || moveStatus.isPending || nextStatus === task.status}>
                  {moveStatus.isPending ? t("work.saving") : t("work.move")}
                </button>
              </div>
              <form
                key={`${task.id}:${task.revision}`}
                className={styles.detailForm}
                onInput={() => setDetailState("unsaved")}
                onSubmit={(event) => {
                  event.preventDefault();
                  try {
                    setFormError(null);
                    setDetailState("saving");
                    saveTask.mutate(readTaskDraft(new FormData(event.currentTarget)));
                  } catch {
                    setDetailState("unsaved");
                    setFormError(t("work.error"));
                  }
                }}
              >
                <label><span>{t("work.taskTitle")}</span><input name="title" defaultValue={task.title} maxLength={500} /></label>
                <label><span>{t("work.description")}</span><textarea name="descriptionMarkdown" defaultValue={task.descriptionMarkdown} maxLength={1_048_576} rows={8} /></label>
                <div className={styles.formGrid}>
                  <label><span>{t("work.priority")}</span><select name="priority" defaultValue={task.priority}>{TASK_PRIORITIES.map((value) => <option key={value} value={value}>{t(`work.priorities.${value}`)}</option>)}</select></label>
                  <label><span>{t("work.deadline")}</span><input name="dueOn" type="date" defaultValue={task.dueOn ?? ""} /></label>
                </div>
                <label className={styles.checkboxField}>
                  <input
                    name="assignedToMe"
                    type="checkbox"
                    defaultChecked={currentAccountId !== null && task.assigneeIds.includes(currentAccountId)}
                    disabled={currentAccountId === null}
                  />
                  <span>{t("work.assignedToMe")}</span>
                </label>
                <label><span>{t("work.labelsInput")}</span><input name="labels" defaultValue={task.labels.join(", ")} placeholder={t("work.labelsPlaceholder")} /></label>
                <div className={styles.detailActions}>
                  <button type="submit" className={styles.primaryButton} disabled={saveTask.isPending || moveStatus.isPending}>{saveTask.isPending ? t("work.saving") : t("common.save")}</button>
                  <button type="button" className={styles.secondaryButton} onClick={() => archiveTask.mutate()} disabled={archiveTask.isPending || projectQuery.isPending}>{task.archived ? t("work.restore") : t("work.archive")}</button>
                </div>
              </form>
            </>
          ) : (
            <section className={styles.readOnlyDetails} aria-label={t("work.readOnly")}>
              <p><strong>{t("work.status")}</strong> {t(statusLabelKey(task.status))}</p>
              <p><strong>{t("work.priority")}</strong> {t(`work.priorities.${task.priority}`)}</p>
              <p><strong>{t("work.deadline")}</strong> {task.dueOn ?? t("work.noDeadline")}</p>
              <p><strong>{t("work.labelsInput")}</strong> {task.labels.join(", ") || "—"}</p>
            </section>
          )}
          {formError !== null ? <p className={styles.error} role="alert">{formError}</p> : null}
          <section className={styles.descriptionPreview}><h3>{t("work.descriptionPreview")}</h3><MarkdownSafeText value={task.descriptionMarkdown} /></section>
          <section className={styles.activity}>
            <h3>{t("work.commentsAndActivity")}</h3>
            {canMutate ? (
              <form onSubmit={(event) => { event.preventDefault(); if (comment.trim().length > 0) addComment.mutate(); }}>
                <textarea value={comment} onChange={(event) => setComment(event.currentTarget.value)} rows={3} maxLength={1_048_576} placeholder={t("work.commentPlaceholder")} />
                <button type="submit" className={styles.primaryButton} disabled={addComment.isPending}>{t("work.addComment")}</button>
              </form>
            ) : null}
            <div className={styles.timeline}>
              {comments.data?.pages.flatMap((page) => page.items).map((item) => (
                <article key={item.id} className={styles.timelineItem}>
                  <MarkdownSafeText value={item.bodyMarkdown} />
                  <time dateTime={item.createdAt}>{formatWorkDate(item.createdAt, i18n.language)}</time>
                </article>
              ))}
              {comments.hasNextPage ? <button type="button" className={styles.secondaryButton} onClick={() => void comments.fetchNextPage()} disabled={comments.isFetchingNextPage}>{t("work.loadMore")}</button> : null}
              {activity.data?.pages.flatMap((page) => page.items).map((item) => (
                <article key={item.id} className={styles.activityItem}>
                  <strong>{t(`work.activity.${item.type}`)}</strong>
                  <time dateTime={item.createdAt}>{formatWorkDate(item.createdAt, i18n.language)}</time>
                </article>
              ))}
              {activity.hasNextPage ? <button type="button" className={styles.secondaryButton} onClick={() => void activity.fetchNextPage()} disabled={activity.isFetchingNextPage}>{t("work.loadMore")}</button> : null}
            </div>
          </section>
        </>
      ) : null}
    </aside>
  );
}

function MarkdownSafeText({ value }: { readonly value: string }) {
  return <div className={styles.markdown}>{value.split("\n").map((line, index) => <p key={`${index}-${line}`}>{line || "\u00a0"}</p>)}</div>;
}

function formatWorkDate(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale.startsWith("ru") ? "ru-RU" : "en-US", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}
